/* global BigInt */
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { Connection, PublicKey, SystemProgram } from '@solana/web3.js';
import { Program, AnchorProvider, web3, BN } from '@project-serum/anchor';
import { useAnchorWallet, useConnection } from '@solana/wallet-adapter-react';
import idl from '../idl.json';

export const WalletContext = createContext();

const programID = new PublicKey(process.env.REACT_APP_DICE_PROGRAM_ID);
const housePDA = new PublicKey(process.env.REACT_APP_HOUSE_WALLET_ADDRESS);

const CACHE_DURATION = 60000; // 60 seconds cache duration
const SOLANA_RPC_ENDPOINT = 'https://api.devnet.solana.com';

const COMPUTE_BUDGET_PROGRAM_ID = 'ComputeBudget111111111111111111111111111111';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithRetry = async (fetchFunction, maxRetries = 5, initialDelay = 1000) => {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      return await fetchFunction();
    } catch (error) {
      if (error.status === 429 || error.message.includes('rate limit')) {
        const delay = Math.min(initialDelay * Math.pow(2, retries), 32000);
        console.log(`Rate limited. Retrying in ${delay}ms...`);
        await sleep(delay);
        retries++;
      } else {
        throw error;
      }
    }
  }
  throw new Error('Max retries reached');
};

const createConnection = () => {
  const connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
  const originalRequest = connection._rpcRequest.bind(connection);
  connection._rpcRequest = async (method, args) => {
    return fetchWithRetry(() => originalRequest(method, args));
  };
  return connection;
};

function decodeInstruction(programId, data, accounts) {
  const dataBuffer = Buffer.from(data, 'base64');
  
  if (programId === COMPUTE_BUDGET_PROGRAM_ID) {
    if (dataBuffer[0] === 2) {
      const units = dataBuffer.readUInt32LE(1);
      return `Set Compute Unit Limit: ${units} compute units`;
    } else if (dataBuffer[0] === 3) {
      const microLamports = dataBuffer.readBigUInt64LE(1);
      return `Set Compute Unit Price: ${microLamports} micro-lamports per compute unit`;
    }
  } else if (programId === process.env.REACT_APP_DICE_PROGRAM_ID) {
    if (dataBuffer.length >= 10 && dataBuffer[0] === 1) {
      const betAmount = dataBuffer.readBigUInt64LE(1);
      const roll = dataBuffer.readUInt8(9);
      return `Place Bet: ${betAmount / BigInt(1e9)} SOL, Roll: ${roll}`;
    }
  } else if (programId === SystemProgram.programId.toBase58()) {
    if (dataBuffer.length >= 12 && dataBuffer[0] === 2) {
      const lamports = dataBuffer.readBigUInt64LE(4);
      return `Transfer: ${lamports / BigInt(1e9)} SOL`;
    }
  }
  
  return `Unknown instruction: ${dataBuffer.toString('hex')}`;
}

function decodeAccountInfo(accountMeta) {
  let info = '';
  if (accountMeta.isSigner) info += 'Signer, ';
  if (accountMeta.isWritable) info += 'Writable, ';
  return info.slice(0, -2); // Remove trailing comma and space
}

export const WalletProvider = ({ children }) => {
  const { connection: defaultConnection } = useConnection();
  const wallet = useAnchorWallet();
  const [balance, setBalance] = useState(null);
  const [houseWalletBalance, setHouseWalletBalance] = useState(null);
  const [housePerformance, setHousePerformance] = useState(null);
  const [error, setError] = useState(null);
  const cachedData = useRef({ timestamp: 0, data: null });

  const getProvider = useCallback(() => {
    if (!wallet) {
      return null;
    }
    const connection = createConnection();
    return new AnchorProvider(
      connection,
      wallet,
      { preflightCommitment: 'confirmed' }
    );
  }, [wallet]);

  const updateBalance = useCallback(async () => {
    if (wallet?.publicKey) {
      try {
        const connection = createConnection();
        const balance = await connection.getBalance(wallet.publicKey);
        setBalance(balance / web3.LAMPORTS_PER_SOL);
      } catch (error) {
        console.error('Error updating balance:', error);
        setBalance(null);
      }
    }
  }, [wallet]);

  const updateHouseWalletBalance = useCallback(async () => {
    try {
      const connection = createConnection();
      const balance = await fetchWithRetry(() => connection.getBalance(housePDA));
      setHouseWalletBalance(balance / web3.LAMPORTS_PER_SOL);
    } catch (error) {
      console.error('Error updating house wallet balance:', error);
      setHouseWalletBalance(null);
    }
  }, []);

  const fetchLeaderboardAndBets = useCallback(async () => {
    const now = Date.now();
    if (cachedData.current.data && now - cachedData.current.timestamp < CACHE_DURATION) {
      console.log('Using cached house performance data');
      return cachedData.current.data;
    }

    try {
      console.log('Fetching house performance data...');
      const provider = getProvider();
      if (!provider) {
        throw new Error('No provider available');
      }

      const program = new Program(idl, programID, provider);
      console.log('Program ID:', program.programId.toString());
      console.log('House PDA:', housePDA.toString());

      // Fetch the raw account data
      const accountInfo = await provider.connection.getAccountInfo(housePDA);
      if (!accountInfo) {
        throw new Error('House account not found');
      }
      console.log('Raw account data:', accountInfo.data);

      // Attempt to fetch and decode the house state
      let houseState;
      try {
        houseState = await program.account.houseState.fetch(housePDA);
      } catch (decodeError) {
        console.error('Error decoding house state:', decodeError);
        throw new Error('Failed to decode house state');
      }
      console.log('House State:', houseState);

      // Calculate house performance
      const totalBets = new BN(houseState.totalBets).toNumber();
      const totalPayouts = new BN(houseState.totalPayouts).toNumber();
      const houseBalance = new BN(houseState.houseBalance).toNumber();
      
      const housePerformance = {
        totalBets,
        totalPayouts,
        houseBalance: houseBalance / web3.LAMPORTS_PER_SOL,
        profitLoss: (houseBalance - totalPayouts) / web3.LAMPORTS_PER_SOL
      };

      setHousePerformance(housePerformance);

      const result = { housePerformance };
      cachedData.current = { timestamp: now, data: result };
      setError(null);
      return result;
    } catch (error) {
      console.error('Error fetching house performance:', error);
      setError(`Failed to fetch house performance data. Please check your connection and try again. (Error: ${error.message})`);
      return null;
    }
  }, [getProvider]);

  useEffect(() => {
    if (wallet?.publicKey) {
      console.log('Wallet connected. Fetching initial data...');
      fetchLeaderboardAndBets();
      updateBalance();
      updateHouseWalletBalance();
    }

    const intervalId = setInterval(() => {
      if (wallet?.publicKey) {
        console.log('Updating data...');
        fetchLeaderboardAndBets();
        updateBalance();
        updateHouseWalletBalance();
      }
    }, 60000); // Update every 60 seconds

    return () => clearInterval(intervalId);
  }, [wallet, fetchLeaderboardAndBets, updateBalance, updateHouseWalletBalance]);

  return (
    <WalletContext.Provider value={{ 
      publicKey: wallet?.publicKey, 
      balance, 
      updateBalance,
      houseWalletBalance,
      updateHouseWalletBalance,
      housePerformance,
      fetchLeaderboardAndBets,
      error
    }}>
      {children}
    </WalletContext.Provider>
  );
};
